import RequestBuilder, { comparisonTypes } from 'Resources/RequestBuilder';
import ClientAttributes from 'App/babel/attributes/Client';
import ClientResource from 'App/resources/Client';
import type Client from 'App/resources/Model/Client';
import { sortBy } from 'lodash';

/*
	Use resolveClientIds to resolve the id of the related client instead of the id of the relation.

	A parent/sibling/subsidiary lookup that fails is logged and skipped, and a connected client
	that no longer exists (NoSuchEntity) is skipped, so one deleted company cannot kill the list.
*/

type RelationType = 'parent' | 'sibling' | 'subsidiary' | 'connected';

export type RelatedClient = {
	parent?: boolean;
	sibling?: boolean;
	subsidiary?: boolean;
	connected?: boolean;
	sort: number;
	relatedToClientId: number;
	name: string;
	hasActivity: Client['hasActivity'];
	hasOpportunity: Client['hasOpportunity'];
	hasOrder: Client['hasOrder'];
	descriptionChildParent?: string;
	id?: number;
};

const relationSort: { [type in RelationType]: number } = {
	parent: 1,
	sibling: 2,
	subsidiary: 3,
	connected: 4
};

const CONNECTED_CONCURRENCY = 5;

const mapRelatedClient = (
	relClient: Client,
	type: RelationType,
	resolveClientIds?: boolean,
	relObj?: Partial<RelatedClient>
): RelatedClient => {
	const mapped = (relObj || {}) as RelatedClient;
	mapped[type] = true;
	mapped.sort = relationSort[type];
	mapped.relatedToClientId = relClient.id;
	mapped.name = relClient.name;
	mapped.hasActivity = relClient.hasActivity;
	mapped.hasOpportunity = relClient.hasOpportunity;
	mapped.hasOrder = relClient.hasOrder;

	if (resolveClientIds) {
		mapped.id = relClient.id;
	}

	return mapped;
};

export const getAccountRelations = async (
	account: Client | null | undefined,
	resolveClientIds?: boolean
): Promise<RelatedClient[]> => {
	if (!account) {
		return [];
	}

	const relatedClients: RelatedClient[] = [];
	const parentId = account.parent?.id;

	// Siblings are the other children of our parent, subsidiaries are our own children.
	const pushChildrenOf = async (clientId: number, type: 'sibling' | 'subsidiary') => {
		const filters = new RequestBuilder();
		filters.addFilter(ClientAttributes.parent.attr.id, comparisonTypes.Equals, clientId);
		try {
			const { data } = await ClientResource.find(filters.build());
			for (const child of data || []) {
				if (child.id !== account.id) {
					relatedClients.push(mapRelatedClient(child, type, resolveClientIds));
				}
			}
		} catch (e) {
			console.log('find account error', e);
		}
	};

	if (parentId) {
		try {
			const { data } = await ClientResource.get(parentId);
			if (data.name) {
				relatedClients.push(mapRelatedClient(data, 'parent', resolveClientIds));
			}
		} catch (e) {
			console.log('get account error', e);
		}

		await pushChildrenOf(parentId, 'sibling');
	}

	await pushChildrenOf(account.id, 'subsidiary');

	// Same sliding window async.eachLimit gave us - a slow lookup must not stall the others.
	const connectedClients = account.connectedClients || [];
	let next = 0;
	let failed = false;
	const fetchConnected = async () => {
		// eachLimit stopped handing out work on the first error - don't fire the rest of the gets.
		while (next < connectedClients.length && !failed) {
			const relatedClient = connectedClients[next++];
			try {
				const { data } = await ClientResource.get(relatedClient.relatedToClientId);
				relatedClients.push(
					mapRelatedClient(data, 'connected', resolveClientIds, {
						descriptionChildParent: relatedClient.descriptionChildParent,
						id: relatedClient.id // id of the relation
					})
				);
			} catch (err: any) {
				// Let the rest of the relations map if one was bad
				if (err?.response?.data?.error?.key !== 'NoSuchEntity') {
					failed = true;
					throw err;
				}
				console.log('Related client was not found', err);
			}
		}
	};
	await Promise.all(
		Array.from({ length: Math.min(CONNECTED_CONCURRENCY, connectedClients.length) }, fetchConnected)
	);

	return sortBy(relatedClients, 'name');
};
